import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { buildStepContent, isSafeUrl } from '../block-builder';
import { COLOR_RE } from '../guide-design';
import { insertHost, keepHostAttached, readInlinePosition } from '../inline-host';
import type {
  BadgeConfig,
  BadgeTrigger,
  GuideInteractionMetadata,
  GuideRenderContext,
  GuideStep,
} from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';
import { positionArrow } from './floating-arrow';

/** Retardo de gracia del hover: permite mover el mouse del badge al tooltip. */
const HOVER_CLOSE_DELAY_MS = 150;

/**
 * Renderiza una guía tipo `badge` (estilo Pendo): un elemento CHICO —
 * icono/punto/cápsula/imagen — inyectado EN EL FLUJO del DOM junto al ancla
 * (`step.selector` + `style.inlinePosition`), que al hover o click
 * (`style.badgeTrigger`) abre un tooltip con el contenido del paso.
 *
 * Composición (no herencia): reusa `insertHost`/`keepHostAttached` del inline
 * (watchdog contra re-renders de React), `buildStepContent` (bloques) y el
 * posicionamiento Floating UI + caret del tooltip — con `strategy: 'fixed'`
 * porque el host vive DENTRO del flujo del cliente y un ancestro con
 * `overflow: hidden` recortaría un tooltip absoluto.
 *
 * EL BADGE ES PERMANENTE (como en Pendo): nada de lo que haga el usuario lo
 * quita — lo único que se abre y se cierra es el TOOLTIP. La ✕ del tooltip, el
 * hover-out, el click afuera, Esc y los CTA solo cierran el tooltip; el badge
 * queda para volver a consultarlo cuando quiera. Por eso el badge nunca emite
 * `dismissed` (no hay forma de descartarlo) y su frecuencia natural es `always`.
 *
 * Analytics (deliberado):
 *   - `shown` la PRIMERA vez que se ABRE el tooltip (no al inyectar el badge):
 *     mide cuánta gente abrió la ayuda, no en cuántas páginas existía el icono.
 *   - `cta_clicked` en los botones del tooltip (con buttonId si lo tienen).
 */
export class BadgeRenderer extends BaseRenderer {
  private tooltip: HTMLElement | null = null;
  private arrowEl: HTMLElement | null = null;
  private badgeBtn: HTMLButtonElement | null = null;
  private open = false;
  private shownEmitted = false;
  private trigger: BadgeTrigger = 'hover';
  private side: 'top' | 'bottom' | 'left' | 'right' = 'top';
  private closeTimer: number | null = null;
  private stopFloat: (() => void) | null = null;

  async render(context: GuideRenderContext): Promise<void> {
    const step = context.guide.guideSteps[0];
    if (!step) return;

    const selector = step.selector ?? context.guide.activationRules.selector;
    if (typeof selector !== 'string' || selector.length === 0) return;

    const anchor = await waitForElement(selector);
    if (!anchor) return;

    const { host, root } = this.createHost();
    const ownerDocument = root.ownerDocument ?? document;
    this.applyDesign(step.style);
    // Ancho del tooltip personalizable (slider «Ancho» del editor). Var
    // dedicada: --veo-width no distingue "no seteado" (el :host la define).
    const tipWidth = step.style?.width;
    if (typeof tipWidth === 'number' && Number.isFinite(tipWidth)) {
      host.style.setProperty(
        '--veo-badge-tip-width',
        `${Math.min(720, Math.max(220, tipWidth))}px`,
      );
    }
    this.trigger = step.style?.badgeTrigger === 'click' ? 'click' : 'hover';
    const rawSide = step.style?.tooltipPlacement;
    this.side = rawSide === 'bottom' || rawSide === 'left' || rawSide === 'right' ? rawSide : 'top';

    // ── Badge en el flujo del DOM, pegado al ancla ──
    const position = readInlinePosition(step.style);
    host.style.display = 'inline-flex';
    host.style.verticalAlign = 'middle';
    const badge = buildBadgeElement(readBadgeConfig(step), ownerDocument);
    root.appendChild(badge);
    this.badgeBtn = badge;
    insertHost(anchor, host, position);
    this.registerCleanup(keepHostAttached(host, selector, position));

    // ── Tooltip (cerrado hasta el trigger) ──
    const tooltip = ownerDocument.createElement('div');
    tooltip.className = 'veo-tooltip veo-badge-tooltip';
    tooltip.style.display = 'none';

    const emit = (action: 'shown' | 'cta_clicked', meta?: GuideInteractionMetadata): void => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: 0,
        action,
        ...(meta ? { metadata: meta } : {}),
      });
    };

    const buildTooltipContent = (s: GuideStep): HTMLElement =>
      buildStepContent(s, ownerDocument, {
        onCtaClick: (action, target, meta) => {
          emit('cta_clicked', meta);
          if (action === 'url' && target) window.open(target, '_blank', 'noopener,noreferrer');
          // TODO cierra solo el TOOLTIP — el badge es permanente.
          this.closeTooltip();
          // Encadenado: además abre la otra guía.
          if (action === 'guide' && target) context.onOpenGuide?.(target);
        },
        // La ✕ también cierra solo el tooltip (nunca el badge, nunca dismissed).
        onDismiss: () => this.closeTooltip(),
      });

    const content = buildTooltipContent(step);
    tooltip.appendChild(content);

    const arrowEl = ownerDocument.createElement('div');
    arrowEl.className = 'veo-tooltip-arrow';
    tooltip.appendChild(arrowEl);
    root.appendChild(tooltip);
    this.tooltip = tooltip;
    this.arrowEl = arrowEl;

    // Update in-place (preview del editor).
    this.liveContainer = tooltip;
    this.liveContent = content;
    this.liveKey = this.liveKeyOf(step);
    this.liveBuild = buildTooltipContent;

    // ── Trigger ──
    const openNow = (): void => {
      this.cancelClose();
      if (!this.open) {
        this.openTooltip(badge);
        if (!this.shownEmitted) {
          this.shownEmitted = true;
          emit('shown');
        }
      }
    };
    if (this.trigger === 'hover') {
      const scheduleClose = (): void => {
        this.cancelClose();
        this.closeTimer = window.setTimeout(() => this.closeTooltip(), HOVER_CLOSE_DELAY_MS);
      };
      badge.addEventListener('mouseenter', openNow);
      badge.addEventListener('focus', openNow);
      badge.addEventListener('mouseleave', scheduleClose);
      badge.addEventListener('blur', scheduleClose);
      tooltip.addEventListener('mouseenter', () => this.cancelClose());
      tooltip.addEventListener('mouseleave', scheduleClose);
    } else {
      badge.addEventListener('click', () => {
        if (this.open) this.closeTooltip();
        else openNow();
      });
      const onDocClick = (e: MouseEvent): void => {
        if (!this.open) return;
        // El shadow host encapsula badge y tooltip: un click cuyo path no pasa
        // por el host es "afuera".
        if (e.composedPath().includes(host)) return;
        this.closeTooltip();
      };
      document.addEventListener('click', onDocClick, true);
      this.registerCleanup(() => document.removeEventListener('click', onDocClick, true));
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && this.open) this.closeTooltip();
    };
    document.addEventListener('keydown', onKey);
    this.registerCleanup(() => document.removeEventListener('keydown', onKey));

    // En el editor, el autor edita el CONTENIDO del tooltip: abrirlo de entrada
    // (si no, se edita a ciegas). No emite shown (isPreview no trackea igual).
    if (context.isPreview) openNow();
  }

  /** Cambiar de ancla/posición/trigger requiere re-montar y re-cablear. */
  protected override liveKeyOf(step: GuideStep): string {
    const selector = step.selector ?? '';
    const position = readInlinePosition(step.style);
    const trigger = step.style?.badgeTrigger === 'click' ? 'click' : 'hover';
    return `${selector}|${position}|${trigger}`;
  }

  protected override onLiveUpdate(step: GuideStep): void {
    // Re-pintar el badge (kind/color/size) y reposicionar si está abierto.
    if (this.badgeBtn) {
      const doc = this.badgeBtn.ownerDocument;
      const next = buildBadgeElement(readBadgeConfig(step), doc);
      // Conservar listeners del botón: solo se actualiza el interior + clases.
      this.badgeBtn.className = next.className;
      this.badgeBtn.setAttribute('style', next.getAttribute('style') ?? '');
      this.badgeBtn.replaceChildren(...Array.from(next.childNodes));
    }
    const rawSide = step.style?.tooltipPlacement;
    this.side = rawSide === 'bottom' || rawSide === 'left' || rawSide === 'right' ? rawSide : 'top';
    if (this.open && this.badgeBtn) this.position(this.badgeBtn);
  }

  override destroy(): void {
    this.cancelClose();
    this.stopFloat?.();
    this.stopFloat = null;
    this.tooltip = null;
    this.arrowEl = null;
    this.badgeBtn = null;
    this.open = false;
    super.destroy();
  }

  private openTooltip(badge: HTMLElement): void {
    if (!this.tooltip) return;
    this.tooltip.style.display = 'block';
    this.open = true;
    void this.position(badge);
    const reposition = (): void => {
      void this.position(badge);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    this.stopFloat = () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }

  private closeTooltip(): void {
    this.cancelClose();
    if (!this.tooltip || !this.open) return;
    this.tooltip.style.display = 'none';
    this.open = false;
    this.stopFloat?.();
    this.stopFloat = null;
  }

  private cancelClose(): void {
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private async position(badge: HTMLElement): Promise<void> {
    const tooltip = this.tooltip;
    const arrowEl = this.arrowEl;
    if (!tooltip || !arrowEl) return;
    const { x, y, placement, middlewareData } = await computePosition(badge, tooltip, {
      strategy: 'fixed',
      placement: this.side,
      middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowEl })],
    });
    tooltip.style.left = `${x}px`;
    tooltip.style.top = `${y}px`;
    positionArrow(arrowEl, placement, middlewareData.arrow);
  }
}

/** Lee y normaliza `style.badge` (defaults seguros). */
function readBadgeConfig(step: GuideStep): BadgeConfig {
  const raw = step.style?.badge;
  if (!raw || typeof raw !== 'object') return { kind: 'icon' };
  const b = raw as Record<string, unknown>;
  const kind =
    b.kind === 'dot' ||
    b.kind === 'pill' ||
    b.kind === 'text' ||
    b.kind === 'image' ||
    b.kind === 'icon'
      ? b.kind
      : 'icon';
  return {
    kind,
    icon: typeof b.icon === 'string' ? b.icon : null,
    text: typeof b.text === 'string' ? b.text : null,
    imageUrl: typeof b.imageUrl === 'string' ? b.imageUrl : null,
    color: typeof b.color === 'string' && COLOR_RE.test(b.color.trim()) ? b.color.trim() : null,
    size: typeof b.size === 'number' && Number.isFinite(b.size) ? b.size : null,
  };
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Construye el elemento visual del badge. `<button>` por accesibilidad
 * (focus/teclado). Texto por `textContent`; imagen por `isSafeUrl`.
 */
function buildBadgeElement(config: BadgeConfig, doc: Document): HTMLButtonElement {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.className = `veo-badge veo-badge--${config.kind}`;
  btn.setAttribute('aria-label', 'Más información');
  const size = clamp(config.size ?? 18, 8, 48);

  switch (config.kind) {
    case 'dot':
      btn.style.width = `${size}px`;
      btn.style.height = `${size}px`;
      if (config.color) btn.style.background = config.color;
      break;
    case 'pill': {
      btn.textContent = config.text?.slice(0, 24) || 'Nuevo';
      btn.style.fontSize = `${clamp(Math.round(size * 0.62), 8, 24)}px`;
      if (config.color) btn.style.background = config.color;
      break;
    }
    case 'text': {
      // Texto plano, sin cápsula: hereda el color de acento (o el custom).
      btn.textContent = config.text?.slice(0, 32) || 'Nuevo';
      btn.style.fontSize = `${clamp(Math.round(size * 0.7), 9, 28)}px`;
      if (config.color) btn.style.color = config.color;
      break;
    }
    case 'image': {
      if (config.imageUrl && isSafeUrl(config.imageUrl)) {
        const img = doc.createElement('img');
        img.src = config.imageUrl;
        img.alt = '';
        img.style.width = `${size}px`;
        img.style.height = `${size}px`;
        btn.appendChild(img);
      } else {
        // Sin imagen válida degrada a icono para que el badge no sea invisible.
        btn.className = 'veo-badge veo-badge--icon';
        btn.textContent = '?';
        btn.style.width = `${size}px`;
        btn.style.height = `${size}px`;
        btn.style.fontSize = `${clamp(Math.round(size * 0.62), 8, 32)}px`;
      }
      break;
    }
    default: {
      // icon: carácter/emoji dentro de un circulito.
      btn.textContent = (config.icon || 'ℹ️').slice(0, 4);
      btn.style.width = `${size}px`;
      btn.style.height = `${size}px`;
      btn.style.fontSize = `${clamp(Math.round(size * 0.62), 8, 32)}px`;
      if (config.color) btn.style.color = config.color;
      break;
    }
  }
  return btn;
}
